import { useEffect, useRef } from 'react';

type BackHandler = () => boolean | void | Promise<boolean | void>;

interface BackHandlerEntry {
  handler: BackHandler;
  priority: number;
  sequence: number;
}

const handlers = new Map<symbol, BackHandlerEntry>();
let sequence = 0;

export const BACK_PRIORITY = {
  page: 100,
  popup: 200,
  modal: 300
} as const;

export async function dispatchBackHandler() {
  const activeHandlers = Array.from(handlers.values()).sort(
    (left, right) => right.priority - left.priority || right.sequence - left.sequence
  );

  for (const entry of activeHandlers) {
    const handled = await entry.handler();
    if (handled !== false) return true;
  }

  return false;
}

export default function useBackHandler(
  enabled: boolean,
  handler: BackHandler,
  priority: number = BACK_PRIORITY.page
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;

    const key = Symbol('wine-back-handler');
    sequence += 1;
    handlers.set(key, {
      handler: () => handlerRef.current(),
      priority,
      sequence
    });

    return () => {
      handlers.delete(key);
    };
  }, [enabled, priority]);
}
